import { FC, useState } from 'react'

import { Box, Button, Chevron, Spinner, Tabs, Text } from '@factorialco/deprecated-design-system'
import { GroupDetailInfoDocument } from 'generated/resources/graphql'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useForm } from 'modules/core/hooks/useForm'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import EmployeeSelector from 'modules/people_chart/components/EmployeeSelector'
import GroupDetail from 'modules/people_chart/components/GroupDetail'
import GroupDetailForm from 'modules/people_chart/components/GroupDetailForm'
import TeamSettings from 'modules/people_chart/components/GroupSidepanel/TeamSettings'
import MembersDetail from 'modules/people_chart/components/MembersDetail'

import { EditTeamFormValues } from './forms'
import { useUpdateTeam } from './useUpdateTeam'

type Props = {
  canEdit: boolean
  editing: boolean
  canRemove: boolean
  parentId: string | null | undefined
  nextTeamId?: number
  previousTeamId?: number
  onRemove: (teamId: string, parentId: string) => void
  onClose: (groupId: number) => void
  onTeamSelection: (teamId: number) => void
  setOpenHint: (value: boolean) => void
  isChartEditing: boolean
  setSidePanelEditing: (editing: boolean) => void
  onEmployeeSelection: (employeeId: number) => void
  teamId: number
}

const GroupSidepanel: FC<Props> = ({
  canEdit = false,
  editing = false,
  onRemove,
  onClose,
  canRemove = false,
  parentId,
  nextTeamId,
  previousTeamId,
  onTeamSelection,
  setOpenHint,
  isChartEditing = false,
  onEmployeeSelection,
  teamId,
  setSidePanelEditing,
}) => {
  const [currentTab, setCurrentTab] = useState('basic-info-form')
  const { data: teamData, loading: loadingTeamInfo } = useQuery(GroupDetailInfoDocument, {
    variables: { ids: [teamId] },
  })
  const groupData = teamData?.teams?.teamsConnection.edges[0]?.node
  const { updateTeam, loading } = useUpdateTeam()
  const isTeamEditing = isChartEditing || editing

  useUnsafeEffect(() => {
    if (currentTab === 'members' && groupData?.memberships.length === 0) {
      setOpenHint(true)
    }
  }, [currentTab, groupData?.memberships.length, setOpenHint])

  const form = useForm<EditTeamFormValues>({
    enableReinitialize: true,
    defaultValues: {
      name: groupData?.name ?? '',
      description: groupData?.description ?? '',
    },
    onSubmit: async (data, { setErrors }) => {
      if (!groupData?.id) {
        throw new Error('TeamId is not available.')
      }
      const result = await updateTeam({
        variables: {
          id: groupData?.id,
          ...data,
        },
      })

      const handled = handleResult(result, (formErrors) => {
        if (formErrors) {
          setErrors(formErrors)
        }
      })
      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      }
      return handled
    },
  })

  const header = (
    <Box alignItems="center" justifyContent="center" flexDirection="row" flexGrow gap="s24">
      {previousTeamId && (
        <button onClick={() => onTeamSelection(previousTeamId)}>
          <Chevron type="secondary" direction="left" />
        </button>
      )}
      <Text size="300" color="grey800" weight="semibold">
        {groupData?.name ?? ''}
      </Text>
      {nextTeamId && (
        <button onClick={() => onTeamSelection(nextTeamId)}>
          <Chevron type="secondary" direction="right" />
        </button>
      )}
    </Box>
  )

  const handleFormChange = () => {
    form.handleSubmit()
    !loading && !isChartEditing && setSidePanelEditing(false)
  }

  const footer = (
    <Box paddingY={canEdit ? null : 's16'} paddingRight={canEdit ? 's8' : null}>
      {canEdit && isTeamEditing && (
        <Button loading={loading} iconName="save" onClick={handleFormChange}>
          {i18n.t('common.save')}
        </Button>
      )}
      {canEdit && !isTeamEditing && (
        <Button iconName="pencil" onClick={() => setSidePanelEditing(true)}>
          {i18n.t('common.edit')}
        </Button>
      )}
    </Box>
  )

  const handleClose = () => {
    editing && setSidePanelEditing(false)
    onClose(0)
    setOpenHint(false)
  }

  return (
    <Sidepanel
      allowBackgroundInteraction
      open
      title={header}
      footer={footer}
      width="wide"
      onClose={handleClose}
      closeLabel="Close"
      boxPadding={false}
    >
      {!isTeamEditing && (
        <Box gap="s32" padding="s24">
          <GroupDetail basicInfo={groupData} />
          <MembersDetail
            onEmployeeSelection={onEmployeeSelection}
            color="viridian"
            members={groupData}
          />
        </Box>
      )}
      {isTeamEditing && !loadingTeamInfo && groupData && (
        <Box paddingTop="s6">
          <Tabs
            hasAdditionalPadding
            currentId={currentTab}
            onTabClicked={(tabName: string) => setCurrentTab(tabName)}
            key="group-sidepanel"
          >
            <Tabs.Tab
              id="basic-info-form"
              label={i18n.t('people_chart.group_sidepanel.tabs.basic_info')}
            >
              <Box paddingX="s12" paddingY="s24">
                <GroupDetailForm basicInfo={groupData} form={form} loading={loading} />
              </Box>
            </Tabs.Tab>
            <Tabs.Tab id="members" label={i18n.t('people_chart.group_sidepanel.tabs.members')}>
              <EmployeeSelector team={groupData} members={groupData} />
            </Tabs.Tab>
            <Tabs.Tab id="settings" label={i18n.t('people_chart.group_sidepanel.tabs.settings')}>
              <Box paddingX="s12" paddingY="s24">
                <TeamSettings
                  canRemove={canRemove}
                  onRemove={() => onRemove(String(groupData.id), String(parentId))}
                />
              </Box>
            </Tabs.Tab>
          </Tabs>
        </Box>
      )}
      {loadingTeamInfo && (
        <Box height="full" alignItems="center" justifyContent="center">
          <Spinner />
        </Box>
      )}
    </Sidepanel>
  )
}

export default GroupSidepanel
